import React, { useRef, useMemo, useState, useEffect } from "react";
import ForceGraph3D from "react-force-graph-3d";
import * as THREE from "three";
import {
  Graph3DNode,
  Graph3DLink,
  LocalConnection,
  ForceGraphLink,
} from "../../types";
import { getLawToLocalConnections } from "../../api";

interface Props {
  nodes: Graph3DNode[];
  links: Graph3DLink[];
  subGraph: {
    nodes: Graph3DNode[];
    links: Graph3DLink[];
  } | null;
  LAW_TYPES: { name: string; mstCode: string; color: string }[];
  selectedNode: Graph3DNode | null;
  setSelectedNode: (node: Graph3DNode | null) => void;
  setLocalConnections: (connections: LocalConnection[]) => void;
}

const edgeColor = "#ffffff";
const highLightEdgeColor = "#ff0000";
const nodeBaseColor = "#999999";

const LocalLawGraph3D: React.FC<Props> = ({
  nodes,
  links,
  subGraph,
  LAW_TYPES,
  selectedNode,
  setSelectedNode,
  setLocalConnections,
}) => {
  const fgRef = useRef<any>(null);

  // Filter nodes to only include those with is_local_connected == true
  const filteredNodes = useMemo(() => {
    return nodes.filter((node) => node.is_local_connected === true);
  }, [nodes]);

  const nodeIdSet = useMemo(
    () => new Set(filteredNodes.map((n) => n.id)),
    [filteredNodes]
  );

  const processedLinks: ForceGraphLink[] = useMemo(() => {
    return links
      .map((link) => ({
        source: link.source_id,
        target: link.target_id,
        type: link.type ?? "",
      }))
      .filter(
        (link) => nodeIdSet.has(link.source) && nodeIdSet.has(link.target)
      );
  }, [links, nodeIdSet]);
  useEffect(() => {
    if (selectedNode) {
      getLawToLocalConnections(selectedNode.id)
        .then((data) => {
          setLocalConnections(data);
        })
        .catch((error) => {
          console.error("Error fetching local connections:", error);
          setLocalConnections([]);
        });
    } else {
      setLocalConnections([]);
    }
  }, [selectedNode]);

  const onSingleNodeClick = async (node: Graph3DNode) => {
    setSelectedNode(node);
  };

  const getCustomThreeNode = (node: Graph3DNode) => {
    // const isHighlighted =
    //   highlightNodes.size === 0 || highlightNodes.has(node.id);
    const isHighlighted = true;
    const material = new THREE.MeshStandardMaterial({
      color: getBaseColorByLawName(node),
      opacity: isHighlighted ? 1 : 0.2,
      transparent: true,
    });

    const geometry = new THREE.SphereGeometry(4, 16, 16);
    const mesh = new THREE.Mesh(geometry, material);

    return mesh;
  };

  const getBaseColorByLawName = (node: Graph3DNode) => {
    const lawType = LAW_TYPES.find((law) => law.mstCode === node.mst_code);
    if (lawType) {
      return lawType.color;
    } else {
      return nodeBaseColor;
    }
  };

  const getNodeLabel = (node: Graph3DNode) => {
    return `${node.law_name ?? ""} ${node.number ?? ""}\n${
      node.short_content ? node.short_content.slice(0, 100) : ""
    }...`;
  };

  const getLinkWidth = (link: ForceGraphLink) => {
    // const isHighlighted =highlightLinks.has(`${link.source}-${link.source}`)
    const isHighlighted = true;
    return isHighlighted ? 0.7 : 0.2;
  };

  const getLinkColor = (link: ForceGraphLink) => {
    // const isHighlighted = highlightLinks.has(`${link.source}-${link.source}`)
    const isHighlighted = true;
    return isHighlighted ? highLightEdgeColor : edgeColor;
  };

  const graphDataRef = useRef({ nodes: filteredNodes, links: processedLinks });

  useEffect(() => {
    console.log(filteredNodes);
    graphDataRef.current = { nodes: filteredNodes, links: processedLinks };
  }, [filteredNodes, processedLinks]);

  return (
    <div style={{ height: "100vh", width: "100%", position: "relative" }}>
      <ForceGraph3D
        ref={fgRef}
        graphData={graphDataRef.current}
        backgroundColor="#ffffff"
        linkDirectionalArrowLength={2.5}
        linkDirectionalArrowRelPos={1}
        nodeLabel={(node: Graph3DNode) => getNodeLabel(node)}
        linkWidth={(link) => getLinkWidth(link)}
        linkColor={(link) => getLinkColor(link)}
        nodeThreeObject={(node: Graph3DNode) => {
          return getCustomThreeNode(node);
        }}
        onNodeClick={onSingleNodeClick}
      />
    </div>
  );
};

export default LocalLawGraph3D;
